import { defineStore } from 'pinia';
import { useFiltersStore } from '@/stores/filters';
import { ref, watch } from 'vue';
import { apiCall } from '@/utils/apiAxios';
import { useAgentStore } from '@/stores/agent';
import { getDateRange } from '@/utils/dates';

export const useDashboardStore = defineStore('dashboard', () => {
    const filtersStore = useFiltersStore();
    const agentStore = useAgentStore();

    const filterRageDates = ref('this_year');
    const filterStartDate = ref(getDateRange(filterRageDates.value).startDate);
    const filterEndDate = ref(getDateRange(filterRageDates.value).endDate);
    const filterInsuranceId = ref(null);
    const typePrima = ref('1');
    const dateType = ref('1');

    const chartDataNetPremium = ref({
        categories: [],
        series: [],
        loading: false
    });

    const chartDataCountPolicies = ref({
        categories: [],
        series: [],
        loading: false
    });

    const countersData = ref({
        total_premiums: '0',
        pylc_premiums: '0',
        no_pylc_premiums: '0',
        loading: false
    });

    const chartDataNetPremiumPie = ref({
        categoriesInsurance: [],
        seriesInsurance: [],
        colorInsurance: [],
        categoriesCategory: [],
        seriesCategory: [],
        categoriesKind: [],
        seriesKind: [],
        loading: false
    });

    const countDataPoliciesPie = ref({
        categoriesInsurance: [],
        seriesInsurance: [],
        colorInsurance: [],
        categoriesCategory: [],
        seriesCategory: [],
        categoriesKind: [],
        seriesKind: [],
        loading: false
    });

    const chartDataClientsTreemap = ref({
        loading: false,
        series: []
    });

    const getChartDataNetPremium = async () => {
        if (chartDataNetPremium.value.series.length == 0) {
            getChartDataNetPremiumApi();
        }
    };

    const getChartDataNetPremiumApi = async () => {
        chartDataNetPremium.value.loading = true;

        const data_post = {
            agent_hash: agentStore.agentHash,
            start_date: filterStartDate.value,
            end_date: filterEndDate.value,
            insurance_id: filterInsuranceId.value,
            is_pylc: filtersStore.selectedWallet,
            type_prima: typePrima.value,
            date_type: dateType.value
        };

        const { request } = await apiCall({
            method: 'post',
            url: `/brokerin/api/dashboards/emit_premium_chart`,
            data: data_post
        });

        const response = await request;
        if (response.status == 1) {
            chartDataNetPremium.value.categories = response.categories;
            chartDataNetPremium.value.series = response.series;
        } else {
            console.error('error al obtener los datos (getChartDataNetPremium)');
        }
        chartDataNetPremium.value.loading = false;
        return;
    };

    const getChartDataCountPolicies = async () => {
        if (chartDataCountPolicies.value.series.length == 0) {
            getChartDataCountPoliciesApi();
        }
    };

    const getChartDataCountPoliciesApi = async () => {
        chartDataCountPolicies.value.loading = true;

        const data_post = {
            agent_hash: agentStore.agentHash,
            start_date: filterStartDate.value,
            end_date: filterEndDate.value,
            insurance_id: filterInsuranceId.value,
            is_pylc: filtersStore.selectedWallet,
            type_prima: typePrima.value,
            date_type: dateType.value
        };

        const { request } = await apiCall({
            method: 'post',
            url: `/brokerin/api/dashboards/count_policies_chart`,
            data: data_post
        });

        const response = await request;
        if (response.status == 1) {
            chartDataCountPolicies.value.categories = response.categories;
            chartDataCountPolicies.value.series = response.series;
        } else {
            console.error('error al obtener los datos (getChartDataCountPolicies)');
        }
        chartDataCountPolicies.value.loading = false;
        return;
    };

    const getCountersData = async () => {
        if (
            countersData.value.total_premiums == '0' ||
            countersData.value.pylc_premiums == '0' ||
            countersData.value.no_pylc_premiums == '0'
        ) {
            getCountersDataApi();
        }
    };

    const getCountersDataApi = async () => {
        countersData.value.loading = true;

        const data = {
            agent_hash: agentStore.agentHash,
            start_date: filterStartDate.value,
            end_date: filterEndDate.value,
            insurance_id: filterInsuranceId.value,
            is_pylc: filtersStore.selectedWallet,
            type_prima: typePrima.value,
            date_type: dateType.value
        };

        const { request } = await apiCall({
            method: 'get',
            url: `/brokerin/api/dashboards/total_premiums`,
            data: {},
            params: data
        });

        const response = await request;
        if (response.status == 1) {
            countersData.value.total_premiums = response.premiums.total;
            countersData.value.pylc_premiums = response.premiums.pylc;
            countersData.value.no_pylc_premiums = response.premiums.personal;
        } else {
            console.error('error al obtener los datos (getCountersData)');
        }

        countersData.value.loading = false;
        return;
    };

    const getChartDataNetPremiumPie = async () => {
        if (chartDataNetPremiumPie.value.categoriesInsurance.length == 0) {
            getChartDataNetPremiumPieApi();
        }
    };

    const getChartDataNetPremiumPieApi = async () => {
        chartDataNetPremiumPie.value.loading = true;

        const data_post = {
            agent_hash: agentStore.agentHash,
            start_date: filterStartDate.value,
            end_date: filterEndDate.value,
            insurance_id: filterInsuranceId.value,
            is_pylc: filtersStore.selectedWallet,
            type_prima: typePrima.value,
            date_type: dateType.value
        };

        const { request } = await apiCall({
            method: 'post',
            url: `/brokerin/api/dashboards/pie_charts_premiums`,
            data: data_post
        });

        const response = await request;
        if (response.status == 1) {
            chartDataNetPremiumPie.value.categoriesInsurance = response.response.insurances_chart.labels;
            chartDataNetPremiumPie.value.seriesInsurance = response.response.insurances_chart.series.map(Number);
            chartDataNetPremiumPie.value.colorInsurance = response.response.insurances_chart.colors;
            chartDataNetPremiumPie.value.categoriesCategory = response.response.categories_chart.labels;
            chartDataNetPremiumPie.value.seriesCategory = response.response.categories_chart.series.map(Number);
            chartDataNetPremiumPie.value.categoriesKind = response.response.kinds_chart.labels;
            chartDataNetPremiumPie.value.seriesKind = response.response.kinds_chart.series.map(Number);
        } else {
            console.error('error al obtener los datos (getChartDataNetPremiumPie)');
        }

        chartDataNetPremiumPie.value.loading = false;
        return;
    };

    const getChartDataCountPoliciesPie = async () => {
        if (countDataPoliciesPie.value.categoriesInsurance.length == 0) {
            getChartDataCountPoliciesPieApi();
        }
    };

    const getChartDataCountPoliciesPieApi = async () => {
        countDataPoliciesPie.value.loading = true;

        const data_post = {
            agent_hash: agentStore.agentHash,
            start_date: filterStartDate.value,
            end_date: filterEndDate.value,
            insurance_id: filterInsuranceId.value,
            is_pylc: filtersStore.selectedWallet,
            type_prima: typePrima.value,
            date_type: dateType.value
        };

        const { request } = await apiCall({
            method: 'post',
            url: `/brokerin/api/dashboards/pie_charts_policies`,
            data: data_post
        });

        const response = await request;
        if (response.status == 1) {
            countDataPoliciesPie.value.categoriesInsurance = response.response.insurances_chart.labels;
            countDataPoliciesPie.value.seriesInsurance = response.response.insurances_chart.series.map(Number);
            countDataPoliciesPie.value.colorInsurance = response.response.insurances_chart.colors;
            countDataPoliciesPie.value.categoriesCategory = response.response.categories_chart.labels;
            countDataPoliciesPie.value.seriesCategory = response.response.categories_chart.series.map(Number);
            countDataPoliciesPie.value.categoriesKind = response.response.kinds_chart.labels;
            countDataPoliciesPie.value.seriesKind = response.response.kinds_chart.series.map(Number);
        } else {
            console.error('error al obtener los datos (getChartDataCountPoliciesPie)');
        }

        countDataPoliciesPie.value.loading = false;
        return;
    };

    const getChartDataClientsTreemap = async () => {
        if (chartDataClientsTreemap.value.series.length == 0) {
            getChartDataClientsTreemapApi();
        }
    };

    const getChartDataClientsTreemapApi = async () => {
        chartDataClientsTreemap.value.loading = true;

        const data = {
            agent_hash: agentStore.agentHash,
            start_date: filterStartDate.value,
            end_date: filterEndDate.value,
            insurance_id: filterInsuranceId.value,
            is_pylc: filtersStore.selectedWallet,
            type_prima: typePrima.value,
            date_type: dateType.value
        };

        const { request } = await apiCall({
            method: 'get',
            url: `/brokerin/api/dashboards/top_clients_policies_chart`,
            data: {},
            params: data
        });

        const response = await request;
        if (response.status == 1) {
            chartDataClientsTreemap.value.series = response.result;
        } else {
            console.error('error al obtener los datos (getCountersData)');
        }

        chartDataClientsTreemap.value.loading = false;
        return;
    };

    const getData = async (type: number) => {
        filterStartDate.value = getDateRange(filterRageDates.value).startDate;
        filterEndDate.value = getDateRange(filterRageDates.value).endDate;
        if (type == 2) {
            getCountersDataApi();
        }

        await Promise.allSettled([getChartDataNetPremiumApi(), getChartDataCountPoliciesApi(), getChartDataNetPremiumPieApi(), getChartDataCountPoliciesPieApi(), getChartDataClientsTreemapApi()]);
    };

    const resetAllVariables = () => {
        chartDataNetPremium.value.categories = [];
        chartDataNetPremium.value.series = [];
        chartDataCountPolicies.value.categories = [];
        chartDataCountPolicies.value.series = [];
        countersData.value.total_premiums = '0';
        countersData.value.pylc_premiums = '0';
        countersData.value.no_pylc_premiums = '0';
    };

    watch(
        () => filtersStore.selectedWallet,
        () => getData(1)
    );
    watch(
        () => [filterRageDates.value, filterInsuranceId.value, typePrima.value, dateType.value],
        () => getData(2)
    );

    async function getAllData() {
        await Promise.allSettled([
            getCountersData(),
            getChartDataNetPremium(),
            getChartDataCountPolicies(),
            getChartDataNetPremiumPie(),
            getChartDataCountPoliciesPie(),
            getChartDataClientsTreemap()
        ]);
    }

    return {
        chartDataNetPremium,
        chartDataCountPolicies,
        countersData,
        chartDataNetPremiumPie,
        countDataPoliciesPie,
        chartDataClientsTreemap,
        typePrima,
        dateType,
        filterStartDate,
        filterEndDate,
        filterInsuranceId,
        filterRageDates,
        getChartDataNetPremium,
        getChartDataCountPolicies,
        getCountersData,
        getChartDataNetPremiumPie,
        getChartDataCountPoliciesPie,
        getChartDataClientsTreemap,
        resetAllVariables,
        getAllData
    };
});
